import { BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend, ResponsiveContainer } from 'recharts'
import Box from '@cloudscape-design/components/box'
import type { BenchmarkRun } from '@/types'
import { useDesignTokens } from '@/hooks/useDesignTokens'

interface Props { run: BenchmarkRun }

const SEGMENTS = ['TTFT', 'Guardrail In', 'Guardrail Out', 'Streaming'] as const

export function WaterfallChart({ run }: Props) {
  const tokens = useDesignTokens()

  const data = run.stats.map((stat) => ({
    profile: stat.profileName,
    TTFT: Math.round(stat.avgTtftSec * 1000),
    'Guardrail In': Math.round(stat.avgGuardrailInputMs),
    'Guardrail Out': Math.round(stat.avgGuardrailOutputMs),
    Streaming: Math.round((stat.avgTotalSec - stat.avgTtftSec) * 1000),
  }))

  return (
    <div style={styles.container}>
      <h3 style={styles.title}>Average Latency Breakdown (ms)</h3>
      <Box variant="small" color="text-body-secondary" padding={{ bottom: 's' }}>
        Averaged across all iterations per profile. Shows where time is spent: waiting for the first token (TTFT),
        guardrail processing (if enabled), and streaming the response.
      </Box>
      <ResponsiveContainer width="100%" height={Math.max(200, data.length * 60 + 80)}>
        <BarChart data={data} layout="vertical" margin={{ left: 10, right: 20 }}>
          <CartesianGrid stroke={tokens.lineGrid} strokeDasharray="3 3" />
          <XAxis type="number" stroke={tokens.axisStroke} tick={{ fill: tokens.tickColor }} />
          <YAxis type="category" dataKey="profile" width={200} tick={{ fill: tokens.tickColor, fontSize: 12 }} />
          <Tooltip
            contentStyle={{ backgroundColor: tokens.tooltipBg, border: `1px solid ${tokens.gridColor}` }}
            labelStyle={{ color: tokens.axisLabelColor }}
            formatter={(value: number) => `${value} ms`}
          />
          <Legend />
          {SEGMENTS.map((seg, i) => (
            <Bar key={seg} dataKey={seg} stackId="a" fill={tokens.seriesPalette[i % tokens.seriesPalette.length]} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

const styles: Record<string, React.CSSProperties> = {
  container: { borderRadius: 8, padding: 16, marginBottom: 16 },
  title: { margin: '0 0 12px', fontSize: 15 },
}
